<template>
  <v-layout
    id="plan-edit-form"
    justify-center
    wrap
  >
    <v-flex
      xs12
      md8
      lg8
    >
      <h2 v-if="!plan">
        {{ $t('modules.investment.newPlan') }}
      </h2>
      <h2 v-else>
        {{ $t('modules.investment.editPlan') }}
      </h2>
      <v-card class="pt-7 pl-5 pr-5">
        <v-form>
          <v-layout wrap>
            <helper-cropper
              ref="cropper"
              :aspect-ratio="250 / 250"
              :width="250"
              :height="250"
              @updateAvatar="updateImage"
            />
            <v-flex
              mt-4
              xs12
            >
              <div
                class="image"
                :style="{'border-color': form.bg_color, 'border-width': form.bg_color ? '3px' : '0px'}"
                @click.stop="$refs.image.click()"
              >
                <div
                  v-if="!form.image"
                  class="pt-5 pb-7"
                >
                  <v-icon color="primary">
                    mdi-cloud-upload
                  </v-icon>
                  <p>
                    {{ $t('modules.productUploadImage') }}
                  </p>
                  <p>PNG / JPG</p>
                </div>
                <div style="overflow:hidden;">
                  <img
                    v-if="form.image"
                    alt="image"
                    :src="form.image"
                    width="250"
                  >
                </div>
                <input
                  ref="image"
                  type="file"
                  style="display: none"
                  accept="image/*"
                  @change="setImage"
                >
              </div>
              <div class="text-center mt-2">
                <v-btn
                  v-if="form.image"
                  class="ml-2"
                  outlined
                  small
                  text
                  @click="form.image = ''; $refs.image.value = '';"
                >
                  <v-icon>
                    mdi-bucket-outline
                  </v-icon>
                </v-btn>
              </div>
            </v-flex>
            <v-flex
              xs12
              md12
            >
              <v-text-field
                v-model.trim="form.name"
                :counter="500"
                :error-messages="NameErrors"
                :label="$t('modules.investment.name')+ '*'"
                required
                @input="$v.form.name.$touch()"
                @blur="$v.form.name.$touch()"
              />
            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-text-field
                v-model.trim.number="form.min_deposit"
                :error-messages="MinDepositErrors"
                suffix="USDT"
                :label="$t('modules.investment.minDeposit')+ '*'"
                required
                @input="$v.form.min_deposit.$touch()"
                @blur="$v.form.min_deposit.$touch()"
              />
            </v-flex>
            <v-flex
              v-if="form.status"
              xs12
              md6
            >
              <v-select
                v-model.trim="form.status"
                :items="statuses"
                :item-text="'label'"
                :item-value="'value'"
                :label="$t('modules.investment.statuses.label')"
              />
            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-text-field
                v-model.trim.number="form.max_deposit"
                :error-messages="MaxDepositErrors"
                suffix="USDT"
                :label="$t('modules.investment.maxDeposit')+ '*'"
                required
                @input="$v.form.max_deposit.$touch()"
                @blur="$v.form.max_deposit.$touch()"
              />
            </v-flex>
            <v-flex
              xs6
              md6
            >
              <v-text-field
                v-model.trim.number="form.interest_rate"
                :error-messages="InterestRateErrors"
                suffix="%"
                :label="$t('modules.investment.interestRate')+ '*'"
                required
                @input="$v.form.interest_rate.$touch()"
                @blur="$v.form.interest_rate.$touch()"
              />
            </v-flex>
            <v-flex
              xs6
              md6
            >
              <v-text-field
                v-model.trim.number="form.interest_rate_cycle"
                :error-messages="InterestRateCycleErrors"
                :suffix="$t('globals.datePeriods.days')"
                :label="$t('modules.investment.interestRateCycle')+ '*'"
                required
                @input="$v.form.interest_rate_cycle.$touch()"
                @blur="$v.form.interest_rate_cycle.$touch()"
              />
            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-text-field
                v-model.trim.number="form.max_interest_rate"
                :error-messages="MaxInterestRateErrors"
                suffix="%"
                :label="$t('modules.investment.maxInterestRate')"
                @input="$v.form.max_interest_rate.$touch()"
                @blur="$v.form.max_interest_rate.$touch()"
              >
                <template
                  v-slot:append
                >
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on">
                        mdi-information-outline
                      </v-icon>
                    </template>
                    {{ $t('modules.investment.maxInterestRateTooltip') }}
                  </v-tooltip>
                </template>
              </v-text-field>
            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-select
                v-model.trim="form.excluded_interest_days"
                :items="excludedInterestDays"
                :item-text="'day'"
                :error-messages="ExcludedInterestDaysErrors"
                :label="$t('modules.investment.excludedInterestDays')"
                multiple
              />
            </v-flex>
            <v-flex
              lg12
              xs12
              mt-3
            >
              <b>{{ $t("modules.investment.vestingPeriod") }}</b>
            </v-flex>
            <v-flex
              xs8
            >
              <v-text-field
                v-model.trim.number="form.vesting_period"
                :error-messages="VestingPeriodErrors"
                :label="$t('modules.investment.vestingPeriodLabel')"
                @input="$v.form.vesting_period.$touch()"
                @blur="$v.form.vesting_period.$touch()"
              />
            </v-flex>
            <v-flex
              xs4
            >
              <v-select
                v-model.trim="form.vesting_period_unit"
                :items="periodUnits"
                :item-text="'label'"
                :item-value="'value'"
                :error-messages="VestingPeriodUnitErrors"
                :label="$t('modules.investment.vestingPeriodUnit') + (requiredVestingPeriod?'*':'')"
                @change="$v.form.vesting_period_unit.$touch()"
              />
            </v-flex>
            <v-flex
              lg12
              xs12
              mt-3
            >
              <b>{{ $t("modules.investment.earlyTerminationTitle") }}</b>
            </v-flex>

            <v-flex
              xs12
              md6
            >
              <v-checkbox
                v-model="form.early_termination_pay_interest"
                :label="$t('modules.investment.earlyTerminationPayInterest')"
              />
            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-checkbox
                v-model="form.early_termination"
                :label="$t('modules.investment.earlyTermination')"
              />
            </v-flex>
            <v-flex
              xs6
              md6
            >
              <v-checkbox
                v-model="form.allow_early_interest_withdraw"
                :label="$t('modules.investment.allowEarlyInterestWithdraw')"
              />
            </v-flex>
            <v-flex
              xs6
              md6
            >
              <v-checkbox
                v-model="form.early_termination_penalty"
                :label="$t('modules.investment.earlyTerminationPenalty')"
              />
            </v-flex>
            <v-flex
              v-if="form.early_termination_penalty"
              xs12
              md12
            >
              <v-text-field
                v-model.trim.number="form.early_termination_penalty_pct"
                :error-messages="EarlyTerminationPenaltyPctErrors"
                suffix="%"
                :label="$t('modules.investment.earlyTerminationPenaltyPct')+(requiredPenaltyValue?'*':'')"
                @input="$v.form.early_termination_penalty_pct.$touch()"
                @blur="$v.form.early_termination_penalty_pct.$touch()"
              />
            </v-flex>
          </v-layout>
          <v-layout wrap>
            <v-flex
              lg12
              mt-3
            >
              <b>{{ $t("modules.investment.description") }}</b>
            </v-flex>
            <v-flex>
              <vue-editor
                id="editor"
                v-model="form.description"
                :editor-toolbar="customToolbar"
                @blur="$v.form.description.$touch()"
                @text-change="$v.form.description.$touch()"
              />
              <small
                v-if="DescriptionErrors.length > 0"
                class="text-negative"
              ><b>{{ DescriptionErrors[0] }}</b></small>
            </v-flex>
          </v-layout>
          <v-layout>
            <v-flex
              xs12
              md12
            >
              <v-btn
                fab
                outlined
                small
                text
                @click="$emit('closeEditMode', false)"
              >
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <v-btn
                color="primary"
                style="float:right"
                :disabled="loading"
                :loading="loading"
                @click="save()"
              >
                {{ $t('globals.saveButton') }}
              </v-btn>
            </v-flex>
          </v-layout>
        </v-form>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import INVEST_PLAN_CREATE_MUTATION from "@/graphql/mutations/modules/investPlans/moduleInvestmentPlansCreate.gql";
import INVEST_PLAN_UPDATE_MUTATION from "@/graphql/mutations/modules/investPlans/moduleInvestmentPlansUpdate.gql";

import {validationMixin} from "vuelidate";
import {
  required,
  minLength,
  maxLength,
  minValue,
  maxValue
} from "vuelidate/lib/validators";
import {VueEditor} from "vue2-editor";
import HelperCropper from "@/components/helper/Cropper.vue";

export default {
  name: "ModulePlanEditForm",
  components: {
    HelperCropper,
    VueEditor,
  },
  mixins: [validationMixin],
  props: {
    plan: {
      type: Object,
      default: null
    },
    whitelabelUuid: {
      type: String,
      default: null
    },
  },
  data() {
    return {
      loading: false,
      requiredVestingPeriod: false,
      requiredPenaltyValue: false,
      excludedInterestDays: [
        {value: 'monday', day: this.$t('globals.days.monday')},
        {value: 'tuesday', day: this.$t('globals.days.tuesday')},
        {value: 'wednesday', day: this.$t('globals.days.wednesday')},
        {value: 'thursday', day: this.$t('globals.days.thursday')},
        {value: 'friday', day: this.$t('globals.days.friday')},
        {value: 'saturday', day: this.$t('globals.days.saturday')},
        {value: 'sunday', day: this.$t('globals.days.sunday')},
      ],
      statuses: [
        {
          label:this.$t('modules.investment.statuses.draft'),
          value:"draft",
        },
        {
          label:this.$t('modules.investment.statuses.published'),
          value:"published",
        },
        {
          label:this.$t('modules.investment.statuses.archived'),
          value:"archived",
        }
      ],
      periodUnits: [
        {
          value: 'day',
          label: this.$t('globals.datePeriods.day'),
        },
        {
          value: 'week',
          label: this.$t('globals.datePeriods.week'),
        },
        {
          value: 'month',
          label: this.$t('globals.datePeriods.month'),
        },
        {
          value: 'year',
          label: this.$t('globals.datePeriods.year'),
        }
      ],
      selectedExcludedInterestDays: [],
      customToolbar: [
        [{'header': [1, 2, 3, 4, 5, 6, false]}],
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote'],
        [{'list': 'ordered'}, {'list': 'bullet'}],
        [{'script': 'sub'}, {'script': 'super'}],
        [{'indent': '-1'}, {'indent': '+1'}],
        [{'direction': 'rtl'}],
        [{'color': []}, {'background': []}],
        [{'align': []}, 'link'],

        ['clean']
      ],
      form: {}
    }
  },
  computed: {
    rules() {
      return {
        name: {
          required,
          minLength: minLength(3),
          maxLength: maxLength(500),
        },
        min_deposit: {
          required,
          minValue: minValue(0),
          maxValue: maxValue(9999999999),
        },
        max_deposit: {
          required,
          minValue: minValue(0),
          maxValue: maxValue(9999999999),
        },
        interest_rate: {
          required,
          minValue: minValue(0),
          maxValue: maxValue(100),
        },
        interest_rate_cycle: {
          required,
          minValue: minValue(0),
          maxValue: maxValue(365),
        },
        max_interest_rate: {
          minValue: minValue(0),
          maxValue: maxValue(9999999999),
        },
        early_termination_penalty_pct: {
          ...(this.requiredPenaltyValue ? {required} : {}),
          minValue: minValue(0),
          maxValue: maxValue(9999999999),
        },
        description: {
          maxLength: maxLength(5000),
        },
        excluded_interest_days: {},
        vesting_period: {
          minValue: minValue(0),
          maxValue: maxValue(365),
        },
        vesting_period_unit: {
          ...(this.requiredVestingPeriod ? {required} : {})
        },
      }
    },
    NameErrors() {
      const errors = [];
      if (!this.$v.form.name.$dirty) return errors;
      !this.$v.form.name.required && errors.push(this.$t("profile.reqField"));
      !this.$v.form.name.minLength &&
      errors.push(this.$t("whitelabel.minError", {char: this.$v.form.name.$params.minLength.min}));
      !this.$v.form.name.maxLength &&
      errors.push(this.$t("whitelabel.maxError", {char: this.$v.form.name.$params.maxLength.max}));
      return errors;
    },
    MinDepositErrors() {
      const errors = [];
      if (!this.$v.form.min_deposit.$dirty) return errors;
      !this.$v.form.min_deposit.required && errors.push(this.$t("profile.reqField"));
      !this.$v.form.min_deposit.minValue &&
      errors.push(this.$t("whitelabel.minValueError", {char: this.$v.form.min_deposit.$params.minValue.min}));
      !this.$v.form.min_deposit.maxValue &&
      errors.push(this.$t("whitelabel.maxValueError", {char: this.$v.form.min_deposit.$params.maxValue.max}));
      return errors;
    },
    MaxDepositErrors() {
      const errors = [];
      if (!this.$v.form.max_deposit.$dirty) return errors;
      !this.$v.form.max_deposit.required && errors.push(this.$t("profile.reqField"));
      !this.$v.form.max_deposit.minValue &&
      errors.push(this.$t("whitelabel.minValueError", {char: this.$v.form.max_deposit.$params.minValue.min}));
      !this.$v.form.max_deposit.maxValue &&
      errors.push(this.$t("whitelabel.maxValueError", {char: this.$v.form.max_deposit.$params.maxValue.max}));
      return errors;
    },
    InterestRateErrors() {
      const errors = [];
      if (!this.$v.form.interest_rate.$dirty) return errors;
      !this.$v.form.interest_rate.required && errors.push(this.$t("profile.reqField"));
      !this.$v.form.interest_rate.minValue &&
      errors.push(this.$t("whitelabel.minValueError", {char: this.$v.form.interest_rate.$params.minValue.min}));
      !this.$v.form.interest_rate.maxValue &&
      errors.push(this.$t("whitelabel.maxValueError", {char: this.$v.form.interest_rate.$params.maxValue.max}));
      return errors;
    },
    InterestRateCycleErrors() {
      const errors = [];
      if (!this.$v.form.interest_rate_cycle.$dirty) return errors;
      !this.$v.form.interest_rate_cycle.required && errors.push(this.$t("profile.reqField"));
      !this.$v.form.interest_rate_cycle.minValue &&
      errors.push(this.$t("whitelabel.minValueError", {char: this.$v.form.interest_rate_cycle.$params.minValue.min}));
      !this.$v.form.interest_rate_cycle.maxValue &&
      errors.push(this.$t("whitelabel.maxValueError", {char: this.$v.form.interest_rate_cycle.$params.maxValue.max}));
      return errors;
    },
    MaxInterestRateErrors() {
      const errors = [];
      if (!this.$v.form.max_interest_rate.$dirty) return errors;
      !this.$v.form.max_interest_rate.minValue &&
      errors.push(this.$t("whitelabel.minValueError", {char: this.$v.form.max_interest_rate.$params.minValue.min}));
      !this.$v.form.max_interest_rate.maxValue &&
      errors.push(this.$t("whitelabel.maxValueError", {char: this.$v.form.max_interest_rate.$params.maxValue.max}));
      return errors;
    },
    VestingPeriodErrors() {
      const errors = [];
      if (!this.$v.form.vesting_period.$dirty) return errors;
      !this.$v.form.vesting_period.minValue &&
      errors.push(this.$t("whitelabel.minValueError", {char: this.$v.form.vesting_period.$params.minValue.min}));
      !this.$v.form.vesting_period.maxValue &&
      errors.push(this.$t("whitelabel.maxValueError", {char: this.$v.form.vesting_period.$params.maxValue.max}));
      return errors;
    },
    VestingPeriodUnitErrors() {
      const errors = [];
      if (!this.$v.form.vesting_period_unit.$dirty) return errors;
      this.requiredVestingPeriod && !this.$v.form.vesting_period_unit.required && errors.push(this.$t("profile.reqField"));
      return errors;
    },
    DescriptionErrors() {
      const errors = [];
      if (!this.$v.form.description.$dirty) return errors;
      !this.$v.form.description.maxLength &&
      errors.push(this.$t("whitelabel.maxError", {char: this.$v.form.description.$params.maxLength.max}));
      return errors;
    },
    ExcludedInterestDaysErrors() {
      const errors = [];
      if (!this.$v.form.excluded_interest_days.$dirty) return errors;
      return errors;
    },
    EarlyTerminationPenaltyPctErrors() {
      const errors = [];
      if (!this.$v.form.early_termination_penalty_pct.$dirty) return errors;
      this.requiredPenaltyValue && !this.$v.form.early_termination_penalty_pct.required && errors.push(this.$t("profile.reqField"));
      !this.$v.form.early_termination_penalty_pct.minValue &&
      errors.push(this.$t("whitelabel.minValueError", {char: this.$v.form.early_termination_penalty_pct.$params.minValue.min}));
      !this.$v.form.early_termination_penalty_pct.maxValue &&
      errors.push(this.$t("whitelabel.maxValueError", {char: this.$v.form.early_termination_penalty_pct.$params.maxValue.max}));
      return errors;
    },
  },
  watch: {
    'form.vesting_period': {
      handler(val) {
        this.requiredVestingPeriod = !!val;
        if (!val) {
          this.form.vesting_period_unit = null;
          this.form.vesting_period = null;
        }
      },
      immediate: true,
    },
    'form.early_termination_penalty': {
      handler(val) {
        this.requiredPenaltyValue = !!val;
        if (!val) {
          this.form.early_termination_penalty_pct = null;
        }
      },
      immediate: true,
    },
  },
  mounted() {
    const form = JSON.parse(JSON.stringify(this.plan))
    if (form && form.uuid) {
      this.selectedPlanUuid = form.uuid;
      delete form.uuid;
      this.form = form
    } else {
      this.form = {
        name: "",
        description: "",
        image: "",
        status: null,

        min_deposit: 0,
        max_deposit: 0,

        interest_rate: 0,
        interest_rate_cycle: 0,
        max_interest_rate: null,
        excluded_interest_days: [],

        vesting_period: null,
        vesting_period_unit: null,

        early_termination: null,
        early_termination_pay_interest: null,
        allow_early_interest_withdraw: null,
        early_termination_penalty: null,
        early_termination_penalty_pct: null,
      }
    }
  },
  validations() {
    return {
      form: this.rules
    };
  },
  methods: {
    setImage(e) {
      if (!this.$refs.cropper.setImage(e)) {
        this.$refs.image.value = "";
      }
    },
    updateImage(image) {
      this.form.image = image;
    },
    save() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return
      }
      this.loading = true;

      function nonNullValues(obj) {
        return Object.fromEntries(
            Object.entries(obj).filter(([_, value]) => value !== null)
        )
      }

      const variables = {
        whitelabel_uuid: this.whitelabelUuid,
        input: !this.plan ? nonNullValues(this.form) : this.form,
      }
      if (this.selectedPlanUuid) {
        variables.plan_uuid = this.selectedPlanUuid
      }
      // Remove vesting_period_unit if it is null.
      if(!variables.input.vesting_period_unit) {
        delete variables.input.vesting_period_unit;
      }
      // Remove early_termination_penalty_pct if it is null.
      if(!variables.input.early_termination_penalty_pct) {
        delete variables.input.early_termination_penalty_pct;
      }
      this.$apollo
          .mutate({
            mutation: !this.plan ? INVEST_PLAN_CREATE_MUTATION : INVEST_PLAN_UPDATE_MUTATION,
            variables
          })
          .then(() => {
            this.$emit('closeEditMode', true)
          })
          .catch(error => {
            this.errorHandler(error);
            this.loading = false;
          });
    }
  },
}
</script>

<style lang="scss">
#editor {
  height: 400px;
}

#plan-edit-form {
  .image {
    display: flex;
    justify-content: center;
    border: 3px solid;
    border-radius: 5px;
    text-align: center;
    cursor: pointer;
    img {
      display: block;
    }

    i {
      margin: 5px 0 0;
      font-size: 26px;
    }

    p {
      margin: 7px 0 0 !important;

      &:first-of-type {
        font-weight: 700;
      }

      line-height: 0.85rem;
    }

    small {
      font-size: 11px;
    }

    &.no-border {
      border: 0 !important;
    }
  }
}
</style>
